import Konva from 'konva';

import { ScaleTransformer } from 'Components/CanvasEditor/models/ScaleTransformer';
import { KonvaImage } from 'Components/CanvasEditor/models/KonvaImage';
import { CropScene } from 'Components/CanvasEditor/models/CropScene';

export class BaseScene extends Konva.Group {
  static sceneId = 'BaseScene';

  private _stage: Konva.Stage;

  private _sceneManager: any;

  private _scaleTransformer: ScaleTransformer;

  private _callbacks: any;

  constructor(stage: Konva.Stage, sceneManager: any, callbacks?: any) {
    super();
    this._stage = stage;
    this._sceneManager = sceneManager;
    this._callbacks = callbacks || {};
    this._scaleTransformer = new ScaleTransformer();
    this.add(this._scaleTransformer);
    this._handlePointerDown = this._handlePointerDown.bind(this);
    this._handleDoubleClick = this._handleDoubleClick.bind(this);
    this._handleTransformStart = this._handleTransformStart.bind(this);
  }

  private _registerEvents() {
    this._stage.on('pointerdown', this._handlePointerDown);
    this._stage.on('pointerdblclick', this._handleDoubleClick);
    this._scaleTransformer.on('transformstart', this._handleTransformStart);
  }

  private _unbindEvents() {
    this._stage.off('pointerdown', this._handlePointerDown);
    this._stage.off('pointerdblclick', this._handleDoubleClick);
    this._scaleTransformer.off('transformstart', this._handleTransformStart);
  }

  private _handlePointerDown({ target }: any) {
    if (target.hasName('_anchor')) {
      return;
    }
    if (target.className === 'Image') {
      const isSelected = this.getSelection() === target;
      if (isSelected) {
        return;
      }
      this.setSelection([target]);
    } else {
      this.setSelection([]);
    }
  }

  private _handleDoubleClick(e: any) {
    const selection = this.getSelection();
    if (e.target === selection) {
      this._sceneManager.goto(CropScene, {
        syncCallback: this._callbacks.syncCallback,
        setActiveCropper: this._callbacks.setActiveCropper,
      });
    }
  }

  private _handleTransformStart() {
    const selection = this.getSelection() as KonvaImage;
    if (!selection) return;

    const activeAnchor = this._scaleTransformer.getActiveAnchor();
    selection.handleTransformStart();

    const transformHandler = () => {
      if (selection) {
        selection.handleTransform(activeAnchor);
      }
    };

    const transformEndHandler = () => {
      this._scaleTransformer.off('transform', transformHandler);
      this._scaleTransformer.off('transformend', transformEndHandler);
      if (selection) {
        selection.handleTransformEnd();
        // Sync the changes to element state
        if (this._callbacks.syncCallback) {
          this._callbacks.syncCallback(selection);
        }
      }
    };

    this._scaleTransformer.on('transform', transformHandler);
    this._scaleTransformer.on('transformend', transformEndHandler);
  }

  hide() {
    super.hide();
    this._unbindEvents();
    return this;
  }

  show() {
    super.show();
    this._registerEvents();
    return this;
  }

  getSelection() {
    return this._scaleTransformer.nodes()[0] ?? null;
  }

  setSelection(target: Konva.Node[]) {
    this._scaleTransformer.nodes(target);
  }
}
